import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/**
 * Props for useMultiValueInput hook
 */
interface UseMultiValueInputProps {
  /** Current value(s) - can be single string or array of strings */
  value: string | string[];
  /** Callback fired when tags change - receives array of string values */
  onChange: (newValue: string | string[]) => void;
  /** Optional validation function - returns valid status and optional error message */
  validate?: (value: string) => { valid: boolean; error?: string };
  /** Whether the input is disabled (no add/remove operations) */
  disabled?: boolean;
}

/**
 * Return value from useMultiValueInput hook
 */
interface UseMultiValueInputReturn {
  /** Current array of tag values */
  tags: string[];
  /** Current text input value (before adding as tag) */
  inputValue: string;
  /** Update the input value */
  setInputValue: (value: string) => void;
  /** Handle keyboard events (Enter to add tag) */
  handleKeyDown: (e: React.KeyboardEvent) => void;
  /** Remove tag at specified index */
  handleRemoveTag: (index: number) => void;
  /** Ref for the container element */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Normalize value to array - defined outside component to avoid recreating
 * @param val - Value to normalize (string or string array)
 * @returns Array of strings
 */
const normalizeValue = (val: string | string[]): string[] => {
  if (Array.isArray(val)) {
    return val;
  }
  return val ? [val] : [];
};

/**
 * Custom hook for managing multi-value tag input state and interactions.
 *
 * Handles:
 * - Tag state management (add, remove, sync with parent)
 * - Input value tracking
 * - Enter key handling for adding tags
 * - Validation with error toasts
 * - Duplicate detection
 * - Disabled state
 *
 * @example
 * ```tsx
 * const multiValue = useMultiValueInput({
 *   value: ['80', '443'],
 *   onChange: (newValues) => console.log(newValues),
 *   validate: (val) => {
 *     const num = parseInt(val, 10);
 *     return num >= 1 && num <= 65535
 *       ? { valid: true }
 *       : { valid: false, error: 'Must be 1-65535' };
 *   },
 *   disabled: false,
 * });
 *
 * // Use in component:
 * <input
 *   value={multiValue.inputValue}
 *   onChange={e => multiValue.setInputValue(e.target.value)}
 *   onKeyDown={multiValue.handleKeyDown}
 * />
 * ```
 */
export const useMultiValueInput = ({
  value,
  onChange,
  validate,
  disabled = false,
}: UseMultiValueInputProps): UseMultiValueInputReturn => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [tags, setTags] = useState<string[]>(() => normalizeValue(value));
  const [inputValue, setInputValue] = useState('');

  // Sync tags when external value changes
  // Use JSON.stringify to avoid infinite loops with array reference changes
  useEffect(() => {
    const normalized = normalizeValue(value);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTags(prevTags => {
      // Only update if the values actually changed
      if (JSON.stringify(prevTags) !== JSON.stringify(normalized)) {
        return normalized;
      }
      return prevTags;
    });
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;

    e.preventDefault();

    if (disabled) return;

    const trimmedValue = inputValue.trim();

    // Ignore empty values
    if (!trimmedValue) return;

    // Check for duplicates
    if (tags.includes(trimmedValue)) {
      toast.error('Value already added');
      return;
    }

    // Run validation if provided
    if (validate) {
      const result = validate(trimmedValue);
      if (!result.valid) {
        toast.error(result.error || 'Invalid value');
        return;
      }
    }

    // Add tag and clear input
    const newTags = [...tags, trimmedValue];
    onChange(newTags);
    setInputValue('');
  };

  const handleRemoveTag = (index: number) => {
    if (disabled) return;

    const newTags = tags.filter((_, i) => i !== index);
    onChange(newTags);
  };

  return {
    tags,
    inputValue,
    setInputValue,
    handleKeyDown,
    handleRemoveTag,
    containerRef,
  };
};
